import React from "react";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsModal from "../clients/Tools/kit/ToolsModal";
import ToolsSelect from "../clients/Tools/kit/ToolsSelect";
import ToolsTabs from "../clients/Tools/kit/ToolsTabs";
import ToolsClientSearch from "../clients/Tools/kit/ToolsClientSearch";
import PlaybookLibraryService from "../../services/PlaybookLibraryService";
import PlaybookRunService from "../../services/PlaybookRunService";
import PlaybookInputFieldService from "../../services/PlaybookInputFieldService";
import UtilityService from "../../services/UtilityService";

import "./run-playbook-chooser-modal.css";

const CLIENT_SCOPES = {
	one: "one",
	allConnected: "allConnected",
};

const CLIENT_SCOPE_TABS = [
	{ key: CLIENT_SCOPES.one, label: "One client" },
	{ key: CLIENT_SCOPES.allConnected, label: "All connected clients" },
];

const NAMES_SHOWN = 4;

class RunPlaybookChooserModal extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			playbookId: props.initialPlaybookId ? String(props.initialPlaybookId) : "",
			client: null,
			clientScope: CLIENT_SCOPES.one,
			connected: null,
			isLoadingConnected: false,
			connectedError: null,
		};
	}

	onPlaybookChanged = async playbookId => {
		const playbook = this.props.playbooks.find(item => String(item.id) === playbookId);
		const asksForValues = PlaybookInputFieldService.fieldsOf({ version: playbook }).length > 0;
		const clientScope = asksForValues ? CLIENT_SCOPES.one : this.state.clientScope;
		await this.update({ playbookId, clientScope, connected: null, connectedError: null });
		if (this.state.clientScope === CLIENT_SCOPES.allConnected) await this.loadConnectedClients();
	};

	onScopeChanged = async clientScope => {
		await this.update({ clientScope });
		const needsConnected = clientScope === CLIENT_SCOPES.allConnected && this.state.playbookId && !this.state.connected;
		if (needsConnected) await this.loadConnectedClients();
	};

	loadConnectedClients = async () => {
		const { playbookId } = this.state;
		if (!playbookId) return;
		await this.update({ isLoadingConnected: true, connectedError: null });
		try {
			const connected = await PlaybookRunService.listConnectedClients({ playbookId: Number(playbookId) });
			if (this.state.playbookId !== playbookId) return;
			await this.update({ connected, isLoadingConnected: false });
		} catch (error) {
			const message = UtilityService.describeRequestError({ error, fallback: "The connected clients could not be found." });
			await this.update({ connected: null, isLoadingConnected: false, connectedError: message });
		}
	};

	onContinue = () => {
		const { playbooks, onChosen } = this.props;
		const { playbookId, client, clientScope, connected } = this.state;
		const agent = playbooks.find(playbook => String(playbook.id) === playbookId);
		const isAll = clientScope === CLIENT_SCOPES.allConnected;
		onChosen({ agent, client: isAll ? null : client, clients: isAll ? connected.clients : null });
	};

	isReady() {
		const { playbookId, client, clientScope, connected } = this.state;
		if (!playbookId) return false;
		if (clientScope === CLIENT_SCOPES.one) return !!client;
		return !!connected && connected.clients.length > 0;
	}

	renderFooter() {
		const { onClose } = this.props;

		return (
			<div className="tk-modal-actions">
				<button type="button" className="tk-button tk-button--secondary" onClick={onClose}>
					Cancel
				</button>
				<button type="button" className="tk-button tk-button--primary" onClick={this.onContinue} disabled={!this.isReady()}>
					Set up the run
					<Icon.ChevronRight size={14} />
				</button>
			</div>
		);
	}

	renderConnected() {
		const { playbookId, connected, isLoadingConnected, connectedError } = this.state;
		if (!playbookId) return <div className="tk-form-help">Choose a playbook first; which clients are connected depends on what it reads.</div>;
		if (isLoadingConnected) return <div className="tk-form-help">Finding the clients with everything this playbook needs linked…</div>;
		if (connectedError) return <div className="tk-notice tk-notice--warning">{connectedError}</div>;
		if (!connected) return null;

		const count = connected.clients.length;
		const needs = connected.requiredSourceLabels.join(" and ");
		const shownNames = connected.clients.slice(0, NAMES_SHOWN).map(client => client.name);
		const moreCount = count - shownNames.length;

		if (count === 0) {
			return (
				<div className="tk-notice tk-notice--warning">
					No client has {needs || "this playbook's sources"} linked yet, so there is nothing to run on.
				</div>
			);
		}

		return (
			<div className="run-playbook-chooser__connected">
				<div className="run-playbook-chooser__connected-count">
					<Icon.Users size={14} />
					{count} of {connected.totalClients} clients
					{needs ? ` have ${needs} linked` : " can run it"}
				</div>
				<div className="tk-form-help">
					{shownNames.join(", ")}
					{moreCount > 0 ? ` and ${moreCount} more` : ""}. Each client gets its own run, grouped under one result.
				</div>
			</div>
		);
	}

	render() {
		const { open, onClose, playbooks } = this.props;
		const { playbookId, client, clientScope } = this.state;
		const choices = PlaybookLibraryService.playbookChoices({ playbooks });
		const chosen = choices.find(choice => choice.value === playbookId);
		const isOne = clientScope === CLIENT_SCOPES.one;
		const playbook = playbooks.find(item => String(item.id) === playbookId);
		const asksForValues = PlaybookInputFieldService.fieldsOf({ version: playbook }).length > 0;
		const oneAtATimeReason = "This playbook asks for values on a form, so it runs for one client at a time.";
		const runInputs = playbook ? PlaybookRunService.runInputsNeededBy({ agent: playbook }) : {};
		const isPeriodNext = !!runInputs.needsPeriod;
		const areSourcesNext = !!playbook && playbook.sources.length > 0;
		let whatComesNext = null;
		if (isPeriodNext && areSourcesNext) whatComesNext = "The period and the sources that are linked come next.";
		else if (isPeriodNext) whatComesNext = "The period comes next.";
		else if (areSourcesNext) whatComesNext = "The sources that are linked come next.";
		const scopeTabs = CLIENT_SCOPE_TABS.map(tab =>
			asksForValues && tab.key === CLIENT_SCOPES.allConnected ? { ...tab, disabled: true, title: oneAtATimeReason } : tab
		);

		return (
			<ToolsModal open={open} onClose={onClose} title="Run a playbook" width={520} footer={this.renderFooter()}>
				<div className="tk-form-field">
					<div className="tk-form-label">
						Playbook<span className="tk-form-required">*</span>
					</div>
					<ToolsSelect
						value={playbookId}
						options={choices}
						onChange={this.onPlaybookChanged}
						placeholder="Choose a playbook…"
						searchable
						searchPlaceholder="Search playbooks"
					/>
					{chosen && chosen.description && <div className="tk-form-help">{chosen.description}</div>}
				</div>
				<div className="tk-form-field">
					<div className="run-playbook-chooser__client-head">
						<div className="tk-form-label">
							Client<span className="tk-form-required">*</span>
						</div>
						<ToolsTabs segmented tabs={scopeTabs} activeKey={clientScope} onSelect={this.onScopeChanged} />
					</div>
					{isOne ? (
						<React.Fragment>
							<ToolsClientSearch
								chosen={client}
								onChoose={choice => this.update({ client: choice.client })}
								onClear={() => this.update({ client: null })}
							/>
							{asksForValues && <div className="tk-form-help">{oneAtATimeReason}</div>}
							{whatComesNext && <div className="tk-form-help">{whatComesNext}</div>}
						</React.Fragment>
					) : (
						this.renderConnected()
					)}
				</div>
			</ToolsModal>
		);
	}
}

export default RunPlaybookChooserModal;
